import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import * as styles from './PodcastTranscript.css';
import type { PodcastTranscriptElementProps, TranscriptLine } from './PodcastTranscript.types';

const POINTER_EVENTS_NONE = { pointerEvents: 'none' as const } satisfies CSSProperties;

const formatTime = (seconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }

  return `${minutes}:${pad(remainingSeconds)}`;
};

const SCROLL_BOTTOM_SAFE_OFFSET_PX = 96;

export function PodcastTranscriptElement(props: PodcastTranscriptElementProps) {
  const { element, children, attributes, className, isLive, autoScroll = true } = props;
  const {
    data,
    padding,
    gap,
    width,

    timestampColor,
    timestampFontSize,
    timestampFontWeight,
    timestampWidth,

    textColor,
    textFontSize,
    textFontWeight,

    activeLineBackground,
    activeTextFontWeight,

    lineBackground,
    lineHoverBackground,
    linePadding,
    lineBorderRadius,

    textFontFamily,

    headingText,
    headingFontFamily,
    headingFontSize,
    headingFontWeight,
    headingColor,
  } = element.attrs || {};

  const transcript = data?.transcript || [];

  const [currentTime, setCurrentTime] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lineRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Find and listen to the audio element on the page.
  // Look for the podcast episode audio player by merge tag id, fall back to any audio element.
  useEffect(() => {
    if (!isLive || transcript.length === 0) return;

    const audio =
      (document.getElementById('audio-podcast.episode.audio_player') as HTMLAudioElement | null) ||
      document.querySelector<HTMLAudioElement>('audio');
    if (!audio) return;

    audioRef.current = audio;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [isLive, transcript.length]);

  const currentLineIndex = useMemo(() => {
    let index = -1;
    for (let i = 0; i < transcript.length; i++) {
      const item = transcript[i];
      if (currentTime >= item.start && currentTime <= item.end) {
        return i;
      }
      if (currentTime >= item.start) {
        index = i;
      }
    }
    return index;
  }, [currentTime, transcript]);

  const handleLineClick = useCallback(
    (line: TranscriptLine) => {
      if (!isLive) return;

      const audio = audioRef.current;
      if (!audio) return;

      audio.currentTime = line.start;
      setCurrentTime(line.start);

      if (audio.paused) {
        audio.play().catch(() => {});
      }
    },
    [isLive],
  );

  // Auto-scroll to keep the active line visible.
  // The transcript container has no fixed height, so the page itself scrolls.
  // We check against the viewport instead of the container bounds.
  useEffect(() => {
    if (!isLive || !autoScroll || currentLineIndex < 0) return;

    const currentLine = lineRefs.current[currentLineIndex];
    if (!currentLine) return;

    const lineRect = currentLine.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const isOutOfView = lineRect.top < 0 || lineRect.bottom > viewportHeight - SCROLL_BOTTOM_SAFE_OFFSET_PX;

    if (isOutOfView) {
      currentLine.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }, [isLive, autoScroll, currentLineIndex]);

  const cssVars = {
    '--padding': padding,
    '--gap': gap,
    '--width': width,
    '--timestamp-color': timestampColor,
    '--timestamp-font-size': timestampFontSize,
    '--timestamp-font-weight': timestampFontWeight,
    '--timestamp-width': timestampWidth,
    '--text-color': textColor,
    '--text-font-size': textFontSize,
    '--text-font-weight': textFontWeight,
    '--active-line-background': activeLineBackground,
    '--active-text-font-weight': activeTextFontWeight,
    '--line-background': lineBackground,
    '--line-hover-background': lineHoverBackground,
    '--line-padding': linePadding,
    '--line-border-radius': lineBorderRadius,
    '--text-font-family': textFontFamily,
  } as CSSProperties;

  if (transcript.length === 0) {
    return null;
  }

  return (
    <div {...attributes} className={classNames('flex flex-col gap-2', className)}>
      {headingText && (
        <h4
          style={{
            fontFamily: headingFontFamily,
            fontSize: headingFontSize,
            fontWeight: headingFontWeight,
            color: headingColor,
            margin: 0,
          }}
        >
          {headingText}
        </h4>
      )}
      <div ref={containerRef} className={styles.transcriptContainer} style={cssVars}>
        {transcript.map((line, index) => {
          const isActive = currentLineIndex === index;

          return (
            <button
              type="button"
              key={`${line.start}-${line.end}`}
              ref={(el) => {
                lineRefs.current[index] = el;
              }}
              className={classNames(styles.transcriptLine, isActive && styles.transcriptLineActive)}
              style={!isLive ? POINTER_EVENTS_NONE : undefined}
              onClick={() => handleLineClick(line)}
            >
              <span className={styles.timestamp}>{formatTime(line.start)}</span>
              <span className={classNames(styles.lineText, isActive && styles.lineTextActive)}>{line.text}</span>
            </button>
          );
        })}
        {children}
      </div>
    </div>
  );
}
